import React, { FC } from 'react';
import cn from 'classnames';

import { Money as MoneyType } from '../../../Money/types';
import { Money, Props as MoneyProps } from '../../../Money';
import { format } from '../../../utils';
import { useTheme } from '../../../theme';
import { CdnIcon } from '@shared/ui';
import CircularProgress from '@mui/material/CircularProgress';

export interface DayProps {
	className?: string;

	date: Date;
	price: MoneyType;

	hidePrice?: boolean;
	isBestPrice?: boolean;
	isSchedule?: boolean;

	selected?: boolean;
	loading?: boolean;
	disabled?: boolean;

	moneyRoundType?: MoneyProps['roundType'];

	onSelect?: (date: Date) => void;
}

const Day: FC<DayProps> = ({
	className,
	date,
	price,
	hidePrice,
	isBestPrice,
	isSchedule,
	selected,
	loading,
	disabled,
	moneyRoundType,
	onSelect
}) => {
	const formattedDate = format(date, 'd MMM'),
		formattedDay = format(date, 'EEEEEE');

	const theme = useTheme('WeekCalendar').DayStyles;

	const isLoadingOrDisabled = loading || disabled;

	const showPrice = price && !hidePrice && !isLoadingOrDisabled;
	const showCircularProgress = loading && !disabled;

	const isSelected = selected && !isLoadingOrDisabled;

	const handleSelect = () => {
		if (!onSelect || isLoadingOrDisabled) {
			return;
		}

		onSelect(date);
	};

	return (
		<div
			className={cn(theme.root, className, {
				[theme.bestPrice]: isBestPrice,
				[theme.schedule]: isSchedule,
				[theme.selected]: isSelected,
				[theme.loading]: loading,
				[theme.disabled]: disabled
			})}
			onClick={handleSelect}
		>
			<div className={theme.content}>
				<div className={theme.date}>{formattedDate}</div>

				<div className={theme.day__wrapper}>
					<div className={theme.dashed} />
					<span className={theme.day}>{formattedDay}</span>
					<div className={theme.dashed} />
				</div>

				<div className={theme.price__wrapper}>
					{showPrice && <Money moneyClassName={theme.price} money={price} roundType={moneyRoundType} />}

					{showCircularProgress && (
						<CircularProgress
							classes={{
								colorPrimary: theme.circularProgress__colorPrimary
							}}
							variant="indeterminate"
							color="primary"
							size={16}
						/>
					)}

					{disabled && <CdnIcon className={theme.disabledIcon} icon="Info/Not included" />}
				</div>
			</div>
		</div>
	);
};

export default Day;
